import React, { useState } from "react";
import {
  NewlyLaunchedOverviewData,
  CreativeOverviewRow,
} from "@/screens/creatives/types";
import { MasonryColumns } from "@/screens/creative-library/components/MasonryColumn";
import { IconCards, IconRocket } from "@tabler/icons-react";
import OverviewSectionWrapper from "../section/OverviewSectionWrapper";
import AdCard from "@/components/common/adCard";
import ATRAction from "@/screens/creatives/components/Overview/card-actions/ATRAction";
import SpendAction from "../card-actions/SpendAction";
import CreativeDetailsDialog from "../creatives-overview/CreativeDetailsDialog";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import NothingToShow from "@/components/common/emptyStates/NothingToShow";
import ShowDetailsAction from "../card-actions/ShowDetailsAction";
import GalleryView from "@/screens/creative-library/components/GalleryView";
import { Button } from "@/components/ui/button";
import ErrorState from "@/components/common/errorStates/ErrorState";
import { cn } from "@/lib/utils";

interface NewlyLaunchedOverviewProps {
  data?: NewlyLaunchedOverviewData;
  title: string;
  loading?: boolean;
  error?: { hasError: boolean; message?: string };
  defaultMetricId?: string;
  onRetry?: () => void;
}

const NewlyLaunchedOverview: React.FC<NewlyLaunchedOverviewProps> = ({
  data,
  title,
  loading = true,
  error,
  defaultMetricId,
  onRetry,
}) => {
  const newlyLaunchedData = data?.newly_launched;
  const maxDisplayedItems = 12; // Limit to 12 items initially
  const [selectedCreative, setSelectedCreative] = useState<CreativeOverviewRow | null>(
    null,
  );
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showGalleryView, setShowGalleryView] = useState(false);

  const cardWidth = 266; // Width of each creative card
  const cardGap = 8; // Gap between cards

  const handleCreativeSelect = (creative: CreativeOverviewRow) => {
    setSelectedCreative(creative);
    setShowDetailsDialog(true);
  };

  const displayedItems = newlyLaunchedData?.rows?.slice(0, maxDisplayedItems) ?? [];
  const hasMoreItems = (newlyLaunchedData?.rows?.length ?? 0) > maxDisplayedItems;

  const renderAdCard = (item: CreativeOverviewRow, index: number) => {
    const firstAd = item.ads?.[0] ?? item.first_ad_metadata;
    const creative = firstAd?.creative;
    const creativeType =
      creative?.videos?.length &&
        creative?.videos?.length > 0 &&
        creative?.videos?.[0]?.permalink_url
        ? "video"
        : item.creative_type === "video"
          ? "video"
          : "image";

    return (
      <AdCard
        key={`${item.creative_name}-${index}`}
        header={{
          title: creative?.account_name || "Unknown Brand",
          icon: creative?.account_icon_url,
        }}
        body={{
          media: {
            ad_id: firstAd?.ad_id || "",
            type: creativeType,
            source:
              creativeType === "image"
                ? creative?.images?.[0]?.permalink_url ||
                item.creative_thumbnail ||
                undefined
                : creative?.videos?.[0]?.permalink_url ||
                item.creative_thumbnail ||
                undefined,
            thumbnail: creative?.thumbnail_url || item.creative_thumbnail || undefined,
            onClick: () => handleCreativeSelect(item),
            actions: [
              {
                component: <ATRAction rank={item.all_time_rank || 0} />,
                position: "top-left",
                visibility: "always",
              },
              {
                component: (
                  <SpendAction
                    prefix={
                      newlyLaunchedData?.columns?.find((col) => col.label === "Spends")
                        ?.prefix
                    }
                    suffix={
                      newlyLaunchedData?.columns?.find((col) => col.label === "Spends")
                        ?.suffix
                    }
                    spend={
                      item[
                        newlyLaunchedData?.columns?.find((col) => col.label === "Spends")
                          ?.key as string
                      ]?.toLocaleString() ?? "-"
                    }
                  />
                ),
                position: "top-left",
                visibility: "always",
              },
              {
                component: (
                  <ShowDetailsAction onClick={() => handleCreativeSelect(item)} />
                ),
                position: "top-right",
                visibility: "playing",
              },
            ],
          },
        }}
      />
    );
  };

  return (
    <OverviewSectionWrapper
      title={title}
      titleIcon={IconRocket}
      showInfoIcon={true}
      tooltipContent="Quick glance of ads which are launched recently."
    >
      {loading ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <WaveLoader />
        </div>
      ) : error?.hasError ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <ErrorState onRetry={onRetry} />
        </div>
      ) : !data || data.newly_launched.count === 0 ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <NothingToShow />
        </div>
      ) : (
        <div className="max-w-[1128px] relative">
          <div className={cn("h-auto", hasMoreItems && "max-h-[800px] overflow-hidden ")}>
            <MasonryColumns
              items={displayedItems}
              cardWidth={cardWidth}
              cardGap={cardGap}
              maxColumns={4}
              centerAlignColumns={false}
              renderItem={renderAdCard}
            />
          </div>

          {/* Show Ads Button */}
          {hasMoreItems && (
            <>
              <div className="absolute z-10 bottom-[31px] left-0 right-0 flex justify-center mt-6">
                <Button
                  variant="default"
                  onClick={() => setShowGalleryView(true)}
                  className="h-[30px] px-[14px] text-white border-none bg-black/80 hover:bg-black shadow-[0_2px_25px_19px_rgba(255,255,255,0.60)] backdrop-blur-[4px]"
                >
                  <IconCards size={14} className="text-white/60" />
                  <span className="">Show All</span>
                </Button>
              </div>
              <div
                className="absolute z-0 bottom-0 left-0 right-0 w-full h-[80px]"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.80) 100%)",
                }}
              />
            </>
          )}
        </div>
      )}

      {/* Creative Details Dialog */}
      <CreativeDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        columnData={newlyLaunchedData?.ad_columns}
        creative={selectedCreative}
        defaultMetricId={defaultMetricId}
        metricColumns={data?.creative_metric_columns}
      />

      {/* Gallery View Dialog */}
      <GalleryView
        open={showGalleryView}
        onOpenChange={setShowGalleryView}
        items={newlyLaunchedData?.rows ?? []}
        renderItem={renderAdCard}
        cardWidth={cardWidth}
        cardGap={cardGap}
        maxColumns={5}
        showCloseButton={true}
      />
    </OverviewSectionWrapper>
  );
};

export default NewlyLaunchedOverview;
